import { ReactElement, useRef } from 'react';
import { NotificationsIcon } from '@/common/components';
import { Outlet, useNavigate } from 'react-router';
import {
    UISidebar,
    UISidebarHeader,
    UISidebarItem,
    UISpacer,
    UIPopover,
    UIMenu,
    UIMenuItem,
    UIMenuDivider,
    UIMenuHeader,
    UIPropoverRef,
    useUINav,
    UINav,
    UIText
} from '@/ui';
import { useCompanies, useCompanyContext } from '@/companies/providers';
import { useMe } from '@/me/providers';
import { useCompanyPermissions, useMyCompanyUser } from '@/companies/hooks';
import { usePagesTranslation } from '../hooks';
import { useLogoutAction } from '@/me/actions';
import { useRegion } from '@/common/providers/region-provider';
import { usePersistedState } from '@/common/hooks';
import { AlertBanner } from '@/tasks/components';
import { UnassignedAlertsProvider } from '@/tasks/providers';
import { NoSubscriptionBanner } from '@/companies/components';
import { InvitesBanner } from '@/companies/components/invites-banner';
import { useFetchTeamCounts } from '@/companies/queries';

export function MainPage(): ReactElement {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const { logout, loading: loggingOut } = useLogoutAction();

    const { me } = useMe();
    const companyUser = useMyCompanyUser();
    const { resolvedRegion } = useRegion();
    const { company } = useCompanyContext();
    const { companies } = useCompanies();
    const { counts: teamCounts } = useFetchTeamCounts(company.id);

    const [collapsed, setCollapsed] = usePersistedState(
        'sidebar-collapsed',
        false
    );

    const popOverRef = useRef<UIPropoverRef>(null);

    const { parts } = useUINav();

    const {
        canViewReports,
        canViewEmergencyBroadcast,
        canViewBilling,
        canViewTeams,
        canViewMembers
    } = useCompanyPermissions();

    return (
        <div
            style={{
                display: 'flex',
                width: '100vw',
                height: '100vh'
            }}
        >
            <UISidebar collapsed={collapsed}>
                <UISpacer />
                <div style={{ marginLeft: 16 }}>
                    <UIPopover
                        ref={popOverRef}
                        placement="bottom_right"
                        trigger="click"
                        triggerNode={
                            <UISidebarHeader
                                image={company.image}
                                companyName={company.name}
                                userName={`${companyUser?.firstName ?? ''} ${companyUser?.lastName ?? ''}`}
                                collapsed={collapsed}
                            />
                        }
                    >
                        <UIMenu
                            footer={
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4,
                                        paddingLeft: 8,
                                        paddingRight: 8
                                    }}
                                >
                                    <UIText variant="small" type="default2">
                                        Version{' '}
                                        {import.meta.env.VITE_APP_VERSION}
                                    </UIText>
                                    <UIText variant="small" type="default2">
                                        Server region:{' '}
                                        {
                                            {
                                                eu: 'United Kingdom/EU',
                                                us: 'United States of America'
                                            }[resolvedRegion]
                                        }
                                    </UIText>
                                </div>
                            }
                        >
                            <UIMenuHeader
                                image={company.image}
                                text={company.name}
                                // Nothing to say until the counts land —
                                // better than flashing "0 teams".
                                subText={
                                    teamCounts
                                        ? `${teamCounts.total} team${teamCounts.total !== 1 ? 's' : ''}`
                                        : undefined
                                }
                            />
                            <UIMenuItem
                                text={t('shared.labels.company_settings')}
                                onClick={() => {
                                    void navigate(
                                        `/company/${company.id}/settings/general`,
                                        {
                                            replace: true
                                        }
                                    );
                                    popOverRef.current?.close();
                                }}
                            />
                            {canViewBilling && (
                                <UIMenuItem
                                    text={t('shared.labels.plan_and_billing')}
                                    onClick={() => {
                                        void navigate(
                                            `/company/${company.id}/settings/billing`,
                                            {
                                                replace: true
                                            }
                                        );
                                        popOverRef.current?.close();
                                    }}
                                />
                            )}

                            {canViewTeams && (
                                <UIMenuItem
                                    text={t('shared.labels.teams')}
                                    onClick={() => {
                                        void navigate(
                                            `/company/${company.id}/settings/teams`,
                                            {
                                                replace: true
                                            }
                                        );
                                        popOverRef.current?.close();
                                    }}
                                />
                            )}
                            {canViewMembers && (
                                <UIMenuItem
                                    text={t('shared.labels.members')}
                                    onClick={() => {
                                        void navigate(
                                            `/company/${company.id}/settings/members`,
                                            {
                                                replace: true
                                            }
                                        );
                                        popOverRef.current?.close();
                                    }}
                                />
                            )}

                            <UIPopover
                                placement="right_bottom"
                                trigger="hover"
                                popoverPadding={10}
                                isNestedPopover
                                triggerNode={
                                    <UIMenuItem
                                        text={t('shared.labels.switch_company')}
                                        chevron
                                    />
                                }
                            >
                                <UIMenu>
                                    {companies.map((company) => (
                                        <UIMenuItem
                                            key={company.id}
                                            text={company.name}
                                            onClick={() => {
                                                void navigate(
                                                    `/company/${company.id}`,
                                                    {
                                                        replace: true
                                                    }
                                                );
                                                popOverRef.current?.close();
                                            }}
                                        />
                                    ))}
                                </UIMenu>
                            </UIPopover>

                            <UIMenuDivider />

                            <UIMenuHeader
                                image={companyUser?.image ?? ''}
                                text={`${companyUser?.firstName ?? ''} ${companyUser?.lastName ?? ''}`}
                                subText={me.email}
                            />
                            <UIMenuItem
                                text={t('shared.labels.account_settings')}
                                onClick={() => {
                                    void navigate(`/account`, {
                                        replace: true
                                    });
                                    popOverRef.current?.close();
                                }}
                            />
                            <UIMenuItem
                                text={t('shared.labels.logout')}
                                loading={loggingOut}
                                disabled={loggingOut}
                                onClick={() => {
                                    void logout().then(() => {
                                        void navigate('/login', {
                                            replace: true
                                        });
                                    });
                                }}
                            />
                        </UIMenu>
                    </UIPopover>
                </div>

                <UISpacer multiplier={2} />

                <UISidebarItem
                    link={`company/${company.id}/dashboard`}
                    text={t('shared.labels.live_dashboard')}
                    icon={'dashboard'}
                    collapsed={collapsed}
                />
                <UISpacer multiplier={0.5} />
                <UISidebarItem
                    link={`company/${company.id}/tasks`}
                    text={t('shared.labels.tasks')}
                    icon={'tasks'}
                    collapsed={collapsed}
                />
                {/* <UISpacer multiplier={0.5} />
                <UISidebarItem
                    link={`company/${company.id}/teams`}
                    text={t('shared.labels.teams')}
                    icon={'teams'}
                    collapsed={collapsed}
                /> */}
                {canViewReports && (
                    <>
                        <UISpacer multiplier={0.5} />
                        <UISidebarItem
                            link={`company/${company.id}/reports`}
                            text={t('shared.labels.reports')}
                            icon={'reports'}
                            collapsed={collapsed}
                        />
                    </>
                )}
                {canViewEmergencyBroadcast && (
                    <>
                        <UISpacer multiplier={0.5} />
                        <UISidebarItem
                            link={`company/${company.id}/emergency-broadcast`}
                            text={t('shared.labels.broadcast')}
                            icon={'message-circle'}
                            collapsed={collapsed}
                        />
                    </>
                )}

                <UISpacer multiplier={0.5} />
                <UISidebarItem
                    link={`company/${company.id}/settings`}
                    text={t('shared.labels.settings')}
                    icon={'settings'}
                    collapsed={collapsed}
                />
                <div
                    style={{
                        position: 'absolute',
                        bottom: 32,
                        left: 0,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end'
                    }}
                >
                    <UISidebarItem
                        externalLink={`https://www.safepointapp.com/store`}
                        text={t('shared.labels.store')}
                        icon={'store'}
                        collapsed={collapsed}
                    />
                    <UISpacer multiplier={0.5} />
                    <UISidebarItem
                        externalLink={`https://support.safepointapp.com/en/`}
                        text={t('shared.labels.support')}
                        icon={'support'}
                        collapsed={collapsed}
                    />
                    <UISpacer multiplier={0.5} />
                    <UISidebarItem
                        onClick={() => setCollapsed(!collapsed)}
                        text={collapsed ? '' : 'Collapse'}
                        icon={collapsed ? 'expand' : 'collapse'}
                        collapsed={collapsed}
                    />
                </div>
            </UISidebar>
            <div style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
                <UnassignedAlertsProvider>
                    <AlertBanner />
                </UnassignedAlertsProvider>
                <NoSubscriptionBanner />
                <InvitesBanner />
                <UINav parts={parts} rightContent={<NotificationsIcon />} />
                <Outlet />
            </div>
        </div>
    );
}
